import { useCallback, useEffect, useState } from "react";
import { api } from "../api";
import Layout from "../components/Layout";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import {
  buildErrorHtml,
  isBlank,
  isPhone,
  isPositiveInt,
} from "../utils/validation";

export default function StudentManagement() {
  const navigate = useNavigate();

  const [students, setStudents] = useState([]);
  const [classes, setClasses] = useState([]);
  const [selectedClass, setSelectedClass] = useState("");

  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);

  const [form, setForm] = useState({
    name: "",
    registrationNumber: "",
    phone: "",
    classId: "",
  });

  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const fetchClasses = useCallback(async () => {
    try {
      const response = await api.get("/classes");
      setClasses(Array.isArray(response.data) ? response.data : []);
    } catch {
      setClasses([]);
    }
  }, []);

  const fetchStudents = useCallback(async (pageNumber = 1, classId = selectedClass) => {
    try {
      let url = `/students?page=${pageNumber}&limit=10`;

      if (classId) {
        url = `/students/class/${classId}?page=${pageNumber}&limit=10`;
      }

      const response = await api.get(url);

      if (Array.isArray(response.data)) {
        setStudents(response.data);
        setPage(1);
        setTotalPages(1);
      } else {
        setStudents(response.data.data || []);
        setPage(response.data.page || 1);
        setTotalPages(response.data.totalPages || 1);
      }
    } catch (error) {
      setStudents([]);
      setPage(1);
      setTotalPages(1);
    }
  }, [selectedClass]);
  useEffect(() => {
    fetchClasses();
    fetchStudents(1);
  }, [fetchClasses, fetchStudents]);



  function openNew() {
    setEditing(null);
    setForm({
      name: "",
      registrationNumber: "",
      phone: "",
      classId: "",
    });
    setShowModal(true);
  }

  function openEdit(student) {
    setEditing(student);
    setForm({
      name: student.name,
      registrationNumber: student.registrationNumber,
      phone: student.phone || "",
      classId: student.classId,
    });
    setShowModal(true);
  }

  async function save() {
    try {
      const errors = [];

      if (isBlank(form.name)) {
        errors.push("Nome é obrigatório.");
      }

      if (isBlank(form.registrationNumber)) {
        errors.push("Matrícula é obrigatória.");
      }

      if (!isPositiveInt(form.classId)) {
        errors.push("Selecione uma turma válida.");
      }

      if (!isBlank(form.phone) && !isPhone(form.phone)) {
        errors.push("Telefone inválido.");
      }

      if (errors.length > 0) {
        Swal.fire({
          icon: "warning",
          title: "Campos inválidos",
          html: buildErrorHtml(errors),
        });
        return;
      }

      if (editing) {
        await api.put(`/students/${editing.id}`, form);
        Swal.fire("Atualizado!", "Aluno atualizado com sucesso.", "success");
      } else {
        await api.post("/students", form);
        Swal.fire("Criado!", "Aluno criado com sucesso.", "success");
      }

      setShowModal(false);
      fetchStudents(page);
    } catch (error) {
      Swal.fire(
        "Erro",
        error.response?.data?.error || "Erro ao salvar.",
        "error"
      );
    }
  }

  async function remove(id) {
    const confirm = await Swal.fire({
      title: "Excluir aluno?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
    });

    if (!confirm.isConfirmed) return;

    await api.delete(`/students/${id}`);
    fetchStudents(page);
  }

  function statusBadge(status) {
    if (status === "MONITORING")
      return <span className="badge bg-warning text-dark">Monitoramento</span>;

    if (status === "EVASION")
      return <span className="badge bg-danger">Evadido</span>;

    return <span className="badge bg-success">Ativo</span>;
  }

  return (
    <Layout>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h4>Gerenciamento de Alunos</h4>
        <button className="btn btn-primary" onClick={openNew}>
          Novo Aluno
        </button>
      </div>

      <div className="card shadow-sm mb-3">
        <div className="card-body">
          <label>Filtrar por Turma</label>

          <select
            className="form-select mt-2"
            value={selectedClass}
            onChange={(e) => {
              const classId = e.target.value;
              setSelectedClass(classId);
              fetchStudents(1, classId);
            }}
          >
            <option value="">Todas</option>
            {classes.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="card shadow-sm">
        <div className="card-body">
          <table className="table table-hover align-middle">
            <thead>
              <tr>
                <th>Nome</th>
                <th>Matrícula</th>
                <th>Telefone</th>
                <th>Turma</th>
                <th>Status</th>
                <th className="text-center">Ações</th>
              </tr>
            </thead>
            <tbody>
              {students.map((s) => (
                <tr key={s.id}>
                  <td>{s.name}</td>
                  <td>{s.registrationNumber}</td>
                  <td>{s.phone || "-"}</td>
                  <td>{s.class?.name}</td>
                  <td>{statusBadge(s.status)}</td>
                  <td className="text-center">
                    <div className="d-flex justify-content-center gap-2">
                      <button
                        className="btn btn-sm btn-outline-primary"
                        onClick={() => openEdit(s)}
                      >
                        Editar
                      </button>

                      <button
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => remove(s.id)}
                      >
                        Excluir
                      </button>

                      <button
                        className="btn btn-sm btn-outline-dark"
                        onClick={() => navigate(`/students/${s.id}/details`)}
                      >
                        Detalhes
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="d-flex justify-content-center align-items-center mt-3">
            <button
              className="btn btn-outline-secondary me-3"
              disabled={page <= 1}
              onClick={() => fetchStudents(page - 1)}
            >
              Anterior
            </button>

            <span>
              Página {page} de {totalPages}
            </span>

            <button
              className="btn btn-outline-secondary ms-3"
              disabled={page >= totalPages}
              onClick={() => fetchStudents(page + 1)}
            >
              Próxima
            </button>
          </div>
        </div>
      </div>

      {showModal && (
        <div className="custom-modal-overlay">
          <div className="custom-modal">
            <h5>{editing ? "Editar Aluno" : "Novo Aluno"}</h5>

            <div className="mb-3">
              <label>Nome</label>
              <input
                className="form-control"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </div>

            <div className="mb-3">
              <label>Matrícula</label>
              <input
                className="form-control"
                value={form.registrationNumber}
                onChange={(e) =>
                  setForm({ ...form, registrationNumber: e.target.value })
                }
              />
            </div>

            <div className="mb-3">
              <label>Telefone</label>
              <input
                className="form-control"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
              />
            </div>

            <div className="mb-3">
              <label>Turma</label>
              <select
                className="form-select"
                value={form.classId}
                onChange={(e) => setForm({ ...form, classId: e.target.value })}
              >
                <option value="">Selecione</option>
                {classes.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="text-end">
              <button
                className="btn btn-secondary me-2"
                onClick={() => setShowModal(false)}
              >
                Cancelar
              </button>

              <button className="btn btn-primary" onClick={save}>
                Salvar
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}




